import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { push, pushSettings } from '@bb/contracts';
import { Button, ErrorState, Field, Select } from '@bb/ui';
import type { PushEnvironmentSettings } from '../../../api/client.ts';
import { DurationField } from '../../../components/DurationField.tsx';
import { NumberInput } from '../../campaigns/trigger/NumberInput.tsx';
import { EditorShell } from '../../editor/EditorShell.tsx';
import { useEditorState } from '../../editor/useEditorState.ts';
import { contractIssues, problemFieldErrors, problemText } from './problems.ts';
import { useApi, type PushSettingsPageData } from './usePushSettingsData.ts';

const MAX_TTL_DAYS = push.PUSH_MAX_TTL_SECONDS / 86_400;
/** Influenced opens (BB-79): the contract's limits of the window, in minutes. */
const INFLUENCED_WINDOW = { min: 1, max: 1_440, default: 60 };
const WINDOW_MESSAGE = `Enter a whole number of minutes from ${INFLUENCED_WINDOW.min} to ${INFLUENCED_WINDOW.max.toLocaleString('en-US')}.`;
const MESSAGES: Record<string, string> = {
  defaultTtlSeconds: `Enter a whole number of seconds from 0 to ${MAX_TTL_DAYS} days.`,
  influencedOpenWindowMinutes: WINDOW_MESSAGE,
};
const PRIORITIES = [
  { value: 'normal', label: 'Normal' },
  { value: 'high', label: 'High' },
];
const LEVELS = [
  { value: 'passive', label: 'Passive' },
  { value: 'active', label: 'Active' },
  { value: 'time-sensitive', label: 'Time-sensitive' },
];
const SOUNDS = [
  { value: 'default', label: 'Default' },
  { value: 'none', label: 'None' },
];

/** The body to save, or one message per field (the contract's limits, checked in the browser). */
function checkDefaults(draft: PushEnvironmentSettings): { body: PushEnvironmentSettings | null; issues: Record<string, string> } {
  const parsed = pushSettings.PushEnvironmentSettings.safeParse(draft);
  if (parsed.success) return { body: parsed.data, issues: {} };
  const issues = contractIssues(parsed.error.issues, MESSAGES);
  if (draft.defaultTtlSeconds > push.PUSH_MAX_TTL_SECONDS) issues.defaultTtlSeconds = `At most ${MAX_TTL_DAYS} days.`;
  return { body: null, issues };
}

/**
 * Defaults a push falls back to when its send settings say «environment default» (BB-73, BB-76): time to live,
 * Android priority, iOS interruption level and sound, and the influenced-open window of push reports (BB-79). Saving
 * needs `integrations.manage`; leaving with unsaved changes asks first.
 */
export function DefaultsForm({ environmentId, data, canEdit }: { environmentId: string; data: PushSettingsPageData; canEdit: boolean }) {
  const api = useApi();
  const queryClient = useQueryClient();
  const editor = useEditorState(data.settings.data);
  const [emptyTtl, setEmptyTtl] = useState(false);
  const [emptyWindow, setEmptyWindow] = useState(false);
  const save = useMutation({
    mutationFn: (body: PushEnvironmentSettings) => api.putPushSettings(environmentId, body),
    onSuccess: (saved) => {
      editor.markSaved(saved);
      queryClient.setQueryData(['push-settings', environmentId], saved);
    },
  });

  if (data.settings.isError) return <ErrorState title="Push defaults could not be loaded" message={problemText(data.settings.error)} onRetry={() => void data.settings.refetch()} />;
  const draft = editor.draft;
  if (!draft) return <p aria-busy="true">Loading push defaults…</p>;

  const checked = checkDefaults(draft);
  const server = problemFieldErrors(save.error);
  const issue = (key: keyof PushEnvironmentSettings) => checked.issues[key] ?? server[key];
  const ready = canEdit && editor.isDirty && checked.body !== null && !emptyTtl && !emptyWindow;
  const update = (patch: Partial<PushEnvironmentSettings>) => {
    if (save.isError || save.isSuccess) save.reset();
    editor.update(patch);
  };
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (ready && checked.body) save.mutate(checked.body);
  };
  const timeSensitiveUnavailable = draft.defaultIosInterruptionLevel === 'time-sensitive' && data.timeSensitiveAvailable === false;
  const formError = save.isError && Object.keys(server).length === 0 ? problemText(save.error) : null;

  return (
    <EditorShell dirty={editor.isDirty} onSave={() => (checked.body ? save.mutateAsync(checked.body) : Promise.reject(new Error('Fix the highlighted fields first.')))} canSave={checked.body !== null} objectLabel="push settings">
      <form className="stack-form push-defaults" aria-label="Push defaults" onSubmit={submit}>
        <DurationField
          label="Time to live"
          seconds={draft.defaultTtlSeconds}
          units={['days', 'hours', 'minutes']}
          hint={`How long FCM and APNs keep trying to reach an offline device; 0 — now or never. At most ${MAX_TTL_DAYS} days.`}
          error={issue('defaultTtlSeconds')}
          disabled={!canEdit}
          onChange={(defaultTtlSeconds) => update({ defaultTtlSeconds })}
          onEmptyChange={setEmptyTtl}
        />
        <Field label="Android priority" hint="High wakes the device right away; providers may throttle marketing pushes sent that way." error={issue('defaultAndroidPriority')}>
          <Select options={PRIORITIES} value={draft.defaultAndroidPriority} disabled={!canEdit} onChange={(e) => update({ defaultAndroidPriority: e.target.value as PushEnvironmentSettings['defaultAndroidPriority'] })} />
        </Field>
        <Field
          label="iOS interruption level"
          hint={timeSensitiveUnavailable ? 'No iOS app declares the time-sensitive entitlement: such pushes are sent as active.' : 'Passive pushes arrive quietly; time-sensitive ones break through Focus when the app has the entitlement.'}
          error={issue('defaultIosInterruptionLevel')}
        >
          <Select options={LEVELS} value={draft.defaultIosInterruptionLevel} disabled={!canEdit} onChange={(e) => update({ defaultIosInterruptionLevel: e.target.value as PushEnvironmentSettings['defaultIosInterruptionLevel'] })} />
        </Field>
        <Field label="Sound" hint="iOS plays the default sound or none; Android follows the channel." error={issue('defaultSound')}>
          <Select options={SOUNDS} value={draft.defaultSound} disabled={!canEdit} onChange={(e) => update({ defaultSound: e.target.value as PushEnvironmentSettings['defaultSound'] })} />
        </Field>
        <Field
          label="Influenced open window (minutes)"
          hint={`Push reports count an app open within this time after a delivered push, without a tap, as an influenced open. ${INFLUENCED_WINDOW.min} to ${INFLUENCED_WINDOW.max.toLocaleString('en-US')} minutes.`}
          error={emptyWindow ? WINDOW_MESSAGE : issue('influencedOpenWindowMinutes')}
        >
          <NumberInput
            min={INFLUENCED_WINDOW.min}
            max={INFLUENCED_WINDOW.max}
            step={1}
            value={emptyWindow ? undefined : (draft.influencedOpenWindowMinutes ?? INFLUENCED_WINDOW.default)}
            disabled={!canEdit}
            onValueChange={(minutes) => {
              setEmptyWindow(minutes === undefined);
              if (minutes !== undefined) update({ influencedOpenWindowMinutes: minutes });
            }}
          />
        </Field>
        {canEdit && (
          <div className="actions">
            <Button type="submit" variant="primary" loading={save.isPending} disabled={!ready}>
              Save defaults
            </Button>
            {editor.isDirty && <span className="hint">Unsaved changes</span>}
          </div>
        )}
        {formError && <p role="alert">{formError}</p>}
        {save.isSuccess && !editor.isDirty && <p role="status">Push defaults saved.</p>}
      </form>
    </EditorShell>
  );
}
